/**
 * SIL-1347 - the marketplace listing card, both buyer tiers. Handoff section 1
 * ("Card"), spec section 4.1.
 *
 * The tier is the SERVER's decision, read off `listing.pricing.tier` /
 * `listing.evidence.tier` - this component never infers it from the buyer's
 * status and never hides a field the API sent. The two tiers are two variants of
 * one card, not two components, because everything except the price cells, the
 * evidence strip and the actions is identical.
 *
 * The rules this card exists to hold:
 *
 *  - **An unverified card has ONE full-width "View details" button** (spec
 *    section 4.1). SIL-1568: with a single action it is the solid primary
 *    (no Buy now exists for that tier); verified cards add Buy now/View
 *    auction as the primary and demote "View details" to a text link.
 *  - **"% vs FMV" is ALWAYS on the verified evidence strip.** The prototype's
 *    `showFmvOnCards` tweak is not built. When no FMV band resolved the item
 *    still renders, saying so - it is never silently dropped.
 *  - **The three Active Verification states render distinctly, and neither
 *    neutral state uses the Verified mark.** That decision is NOT made here: it
 *    is `showsVerifiedMark`/`AvStateChip` (SIL-1348), shared with the listing
 *    detail and the node Passport drawer. This card's only job is to hand the
 *    chip the right `avState`, evidence tier and window verdict - a second copy
 *    of the predicate is how one surface ends up marking unverified hardware as
 *    Verified.
 *
 * ## SIL-1377 - this card tolerates a payload one API version behind
 *
 * `evidence`, `technicalDetails` and `avState` are all things an older API does
 * not send, and this card was crash site two of two (`Cannot read properties of
 * undefined (reading 'tier')` at the `evidence.tier` check). Every such read now
 * goes through `listingShape`, and an absent block means the surface it fed is
 * OMITTED - no evidence strip, no AV chip, no exact price - never filled with a
 * stand-in. The rule the omissions serve is the one SIL-1371 shipped: a degraded
 * render must not read as a positive claim, so it must not be possible to reach
 * the Verified mark by sending LESS. There is no bypass and no second "unknown"
 * path: with no evidence block both of `showsVerifiedMark`'s gating inputs are
 * `null` and it fails closed exactly as designed.
 */
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { CalendarClock } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { ListingImage } from '@siliconx/shared/ui/ListingImage';
import { AuctionCountdown } from '@siliconx/shared/ui/AuctionCountdown';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import type { MarketplaceListing } from '@siliconx/shared/dto/marketplace';
import { verdictFamilyFromWindowCounts } from '@siliconx/shared/verification/presentation';
import { listingPriceView } from '@siliconx/shared/ui/buyer-listing/listingPriceView';
import { isFutureDate, formatMonthYear } from '../../lib/dates';
import { AvStateChip } from '@siliconx/shared/ui/buyer-listing/AvStateChip';
import { marketplaceListingImageModel } from './listingImage';
import {
  LISTING_CARD_BUY_NOW,
  LISTING_CARD_CUSTODY_UNBROKEN,
  LISTING_CARD_CUSTODY_UNKNOWN,
  LISTING_CARD_INDICATIVE_PER_UNIT,
  LISTING_CARD_INDICATIVE_TOTAL,
    LISTING_CARD_NOT_STATED,
  LISTING_CARD_NO_FMV_BAND,
  LISTING_CARD_SKU_MATCH_UNVERIFIED,
  LISTING_CARD_PRICE_PER_UNIT,
  LISTING_CARD_TOTAL_PRICE,
  LISTING_CARD_UNLOCK_NOTE,
  LISTING_CARD_VERIFIED_SELLER,
  LISTING_CARD_VIEW_AUCTION,
  LISTING_CARD_VIEW_DETAILS,
  LISTING_TYPE_LABELS,
  MARKET_LABEL_TEXT,
  listingCardCustodyEvents,
  listingCardNodes,
  listingCardNodesPass,
  listingCardObservations,
  listingCardPctVsFmv,
  listingCardShipsWithin,
} from '@siliconx/shared/ui/buyer-listing/copy/marketplace';
import {
  avStateOf,
  evidenceOf,
  nodeCountFromEvidence,
  pricingOf,
  sellerVerifiedOf,
  statedNumber,
  technicalDetailsOf,
} from '@siliconx/shared/ui/buyer-listing/listingShape';
import {
  assetTypeIdsOf,
  assetTypeLabel,
  locationLabel,
  unitCountOf,
} from './marketplaceListingHelpers';

/**
 * `{gpus}× {model}`, falling back through the display snapshot (handoff).
 *
 * The `displayTitle` fallback is why a skewed payload still has a readable title:
 * with no `technicalDetails` block the two optional keys are simply absent, which
 * is the case this function already handled for a SKU carrying neither attribute.
 */
function cardTitle(listing: MarketplaceListing): string {
  const { gpusPerNode, gpuModel } = technicalDetailsOf(listing);
  if (gpusPerNode !== undefined && gpuModel !== undefined) return `${gpusPerNode}× ${gpuModel}`;
  if (gpuModel !== undefined) return gpuModel;
  return listing.displayTitle ?? listing.assetClassId ?? 'Listing';
}

/**
 * `{n} nodes · {server}` - each half omitted when it has no source.
 *
 * SIL-1377: with no evidence block the node count falls through to `quantity`,
 * the listing's own column, rather than to a zero that would render "0 nodes".
 */
function cardSubtitle(listing: MarketplaceListing): string | null {
  const nodes = nodeCountFromEvidence(evidenceOf(listing));
  const parts: string[] = [];
  if (nodes > 0) parts.push(listingCardNodes(nodes));
  else if (listing.quantity != null && listing.quantity > 0) parts.push(listingCardNodes(listing.quantity));
  const { serverModel } = technicalDetailsOf(listing);
  if (serverModel !== undefined) parts.push(serverModel);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{children}</span>
  );
}

/**
 * SIL-1444: the asset-type badge, visually distinct from `Badge` (outlined
 * vs. filled) per spec Change 4 - a listing-type/condition badge and an
 * asset-type badge must never be visually interchangeable. SIL-1568: no longer
 * brand-colored, so the accent stays isolated on the price and primary CTA.
 */
function AssetTypeBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function StripItem({ children }: { children: ReactNode }) {
  return <span className="text-xs text-muted-foreground">{children}</span>;
}

export function ListingCard({ listing }: { listing: MarketplaceListing }) {
  // SIL-1377 - read through `listingShape`, so each of these is `undefined` when
  // an older API omitted the block rather than a TypeError on first touch.
  const pricing = pricingOf(listing);
  const evidence = evidenceOf(listing);
  const avState = avStateOf(listing);
  const technical = technicalDetailsOf(listing);
  // SIL-1480: 'ask' mode (arbitrage listings) shows one exact price to EVERY
  // viewer — same "not a range" posture as the verified tier for layout
  // purposes (two action buttons, exact-price labels), even though it is not
  // gated on buyer verification at all.
  const forwardDated = isFutureDate(listing.availableOn);

  // The evidence tier the chip needs, per buyer tier: a verified block carries
  // the worst-in-window tier outright, while an unverified block exposes only
  // `isSelfReported` - which is precisely "tier 3". `null` is "tier unknown",
  // which the chip treats as not-Tier-3, matching the server.
  //
  // SIL-1377: an ABSENT block is also `null` here, and that is not a shortcut -
  // it is the same statement ("this surface has no tier to report"), and it is
  // `windowVerdict` below, not this, that withholds the mark.
  const evidenceTier: 1 | 2 | 3 | null =
    evidence === undefined
      ? null
      : evidence.tier === 'verified'
        ? evidence.verificationSummary.tier
        : evidence.isSelfReported
          ? 3
          : null;

  // SIL-1371 - the window VERDICT the chip gates the Verified mark on. `avState`
  // is only "is there a current run"; `listing_av_state()` (962) returns 'active'
  // for a `fail:*` run too, so the chip needs the outcome as well.
  //
  // A verified block carries the window-scoped per-node counts, and
  // `verdictFamilyFromWindowCounts` collapses them worst-first - one failed node
  // among four passing ones withholds the mark for the whole listing.
  //
  // An unverified block carries NO verdict signal at all, and `null` here is the
  // correct, deliberate consequence: migration 956 nulls pass/obs/fail for that
  // tier at the SQL layer, so nothing the API may send this card distinguishes a
  // passing run from `fail:thermal_throttle` (and coverage does not stand in for
  // it - 956 counts a failed node as COVERED, `unverified_nodes` being
  // `verdict IS NULL`). The chip fails closed on `null`, so an unverified buyer
  // gets the neutral "Active Verification" chip rather than a green shield this
  // card cannot substantiate. Restoring the mark for that tier honestly needs a
  // fail signal added to `listing_buyer_evidence` - a disclosure decision, and
  // its own issue.
  // SIL-1377: an ABSENT evidence block is `null` for the same reason the
  // unverified tier is - nothing this card may see distinguishes a passing run
  // from a failed one - and the consequence is identical and deliberate.
  const windowVerdict: string | null =
    evidence !== undefined && evidence.tier === 'verified'
      ? verdictFamilyFromWindowCounts(evidence.verificationSummary)
      : null;

  // SIL-1377: no pricing block, no price. "Not stated" is the card's existing
  // vocabulary for a cell the payload cannot fill, and the alternative - showing
  // the verified variant's EXACT figure, or inventing a range - is precisely the
  // positive claim a degraded render may not make. The label pairs with the
  // value: "Indicative" would promise a bucketed estimate that is not there.
  //
  // SIL-1444: "Price / GPU" -> "Total Price" and "Price / node" -> "Price /
  // Unit" (lot: total / actual asset count via `unitCountOf` /
  // `pricePerUnitUsd` in marketplaceListingHelpers.ts - see that module's
  // doc comment for why this holds uniformly across all three listing
  // types). No "per GPU" language survives on a non-GPU listing.
  const unitCount = unitCountOf(listing);
  const view = pricing === undefined ? undefined : listingPriceView(pricing, evidence, { unitCount });
  const priceLabels =
    view === undefined || view.mode !== 'unverified'
      ? { total: LISTING_CARD_TOTAL_PRICE, unit: LISTING_CARD_PRICE_PER_UNIT }
      : { total: LISTING_CARD_INDICATIVE_TOTAL, unit: LISTING_CARD_INDICATIVE_PER_UNIT };
  const totalPrice = view === undefined ? LISTING_CARD_NOT_STATED : view.total;
  const pricePerUnit = view === undefined ? LISTING_CARD_NOT_STATED : view.perUnit;

  // SIL-1444: state/region of the data center, never the facility name - see
  // `locationLabel`'s doc comment (marketplaceListingHelpers.ts). Replaces
  // the old `evidence.site ?? evidence.region` (verified) / `evidence.region`
  // (unverified) branch, which surfaced `data_center.name` - a facility name
  // like "Ashburn DC1" - on every verified card.
  const location = locationLabel(listing);

  // SIL-1444: the distinct asset-type badges (spec Change 4), labelled with
  // the SAME `assetTypeLabel` the filter card's checkbox group uses.
  const assetTypeIds = assetTypeIdsOf(listing);

  // SIL-1377/SIL-1480: the strip is entirely evidence-derived, so with no
  // evidence block (or no pricing block to pair it with) there is nothing
  // honest to put in it and the band is omitted rather than rendered empty.
  // 'ask' mode also omits it outright — the strip's only pricing content is
  // pct-vs-FMV / market label, both deliberately suppressed for 'ask' mode
  // (spec: "no market label... no pct-vs-FMV").
  const showEvidenceStrip = evidence !== undefined && view !== undefined && view.mode !== 'ask';

  const shipsWithinDays = statedNumber(listing.shipsWithinDays);

  const detailLink = { to: '/listings/$listingId' as const, params: { listingId: listing.id } };

  return (
    <Card
      as="section"
      padding="md"
      data-testid="listing-card"
      data-tier={view?.mode ?? 'unknown'}
      className="flex flex-col gap-3.5 overflow-hidden"
    >
      {/* SIL-1451: family/SKU photo or generic form-factor art, fixed 4:3 so
          the grid never shifts; licence credit overlays the photo. */}
      <ListingImage
        model={marketplaceListingImageModel(listing, cardTitle(listing))}
        variant="card"
      />

      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-xs text-muted-foreground">
          {listing.id.slice(0, 8)}
        </span>
        {/* SIL-1377: the chip's whole job is to place the listing in ONE of three
            AV states, so with no `avState` there is no state to render and the
            chip is omitted. Note it is gated on `avState` - its actual data
            dependency - not on `evidence`: SIL-1345 shipped `avState` and
            SIL-1347 shipped `evidence`, so a payload with the former and not the
            latter is a real skew window, and there the chip renders honestly
            neutral (both gating inputs `null`, `showsVerifiedMark` false). */}
        {avState !== undefined && (
          <AvStateChip
            avState={avState}
            evidenceTier={evidenceTier}
            windowVerdict={windowVerdict}
          />
        )}
      </div>

      <div>
        <Link {...detailLink} className="text-xl font-bold leading-tight tracking-tight text-foreground hover:underline">
          {cardTitle(listing)}
        </Link>
        {cardSubtitle(listing) && (
          <div className="mt-0.5 text-xs text-muted-foreground">{cardSubtitle(listing)}</div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {/* SIL-1480: positive-only — renders only when true, same posture as
            the rest of this card's omit-rather-than-guess discipline. */}
        {sellerVerifiedOf(listing) && (
          <span data-testid="verified-seller-marker">
            <Badge>{LISTING_CARD_VERIFIED_SELLER}</Badge>
          </span>
        )}
        {technical.architecture !== undefined && <Badge>{technical.architecture}</Badge>}
        <Badge>{LISTING_TYPE_LABELS[listing.listingType] ?? listing.listingType}</Badge>
        {listing.conditionBand && <Badge>{conditionLabel(listing.conditionBand)}</Badge>}
        {forwardDated && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <CalendarClock className="size-3" aria-hidden="true" />
            {formatMonthYear(listing.availableOn!)}
          </span>
        )}
        {listing.saleMechanic === 'auction' && listing.auction && (
          <AuctionCountdown closesAt={listing.auction.closesAt} />
        )}
      </div>

      {/* SIL-1444 (spec Change 4): asset-type badges, visually distinct from
          the listing-type/condition badges above (brand color, not muted
          grey) so a buyer can tell "what kind of hardware" from "what kind
          of listing" at a glance. A lot shows one badge per DISTINCT type;
          a single node/sku listing shows its one type. `+N more` caps the
          row at 3 explicit badges rather than wrapping indefinitely. */}
      {assetTypeIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="listing-card-asset-types">
          {assetTypeIds.slice(0, 3).map((id) => (
            <AssetTypeBadge key={id}>{assetTypeLabel(id)}</AssetTypeBadge>
          ))}
          {assetTypeIds.length > 3 && (
            <AssetTypeBadge>+{assetTypeIds.length - 3} more</AssetTypeBadge>
          )}
        </div>
      )}

      {/* SIL-1568 (Von Restorff): the price is the card's isolated figure -
          large, bold, tabular, in foreground (green is reserved for the CTA).
          Unverified ranges render in the same style; the strings are unchanged. */}
      <div className="border-t border-border pt-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {priceLabels.total}
        </div>
        <div className="font-mono text-[30px] font-bold leading-tight tabular-nums text-foreground">
          {totalPrice}
        </div>
        <div className="font-mono text-[13px] text-muted-foreground">
          <span>{pricePerUnit}</span> / unit
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        <span>{location ?? LISTING_CARD_NOT_STATED}</span>
        {' · '}
        <span>
          {shipsWithinDays !== null
            ? listingCardShipsWithin(shipsWithinDays)
            : LISTING_CARD_NOT_STATED}
        </span>
      </div>

      {showEvidenceStrip && (
      <div
        data-testid="evidence-strip"
        className="flex flex-wrap items-center gap-x-3 gap-y-1"
      >
        {evidence.tier === 'verified' && view.mode === 'verified' ? (
          <>
            <StripItem>
              {listingCardNodesPass(
                evidence.verificationSummary.pass,
                evidence.verificationSummary.nodes,
              )}
              {evidence.verificationSummary.obs > 0
                ? ` · ${listingCardObservations(evidence.verificationSummary.obs)}`
                : ''}
            </StripItem>
            <StripItem>{listingCardCustodyEvents(evidence.custodySummary.events)}</StripItem>
            {/* Spec section 4.1: ALWAYS shown. No `showFmvOnCards` tweak. */}
            <StripItem>
              <span data-testid="pct-vs-fmv">
                {view.pctVsFmv !== null
                  ? listingCardPctVsFmv(view.pctVsFmv)
                  : listing.skuMatchUnverified
                    ? LISTING_CARD_SKU_MATCH_UNVERIFIED
                    : LISTING_CARD_NO_FMV_BAND}
              </span>
            </StripItem>
          </>
        ) : evidence.tier === 'unverified' && view.mode === 'unverified' ? (
          <>
            <StripItem>
              {evidence.custodyUnbroken ? LISTING_CARD_CUSTODY_UNBROKEN : LISTING_CARD_CUSTODY_UNKNOWN}
            </StripItem>
            {view.marketLabel && <StripItem>{MARKET_LABEL_TEXT[view.marketLabel]}</StripItem>}
            <StripItem>{LISTING_CARD_UNLOCK_NOTE}</StripItem>
          </>
        ) : null}
      </div>
      )}

      {/* Actions (SIL-1568). The single solid primary is the card's only
          filled element. Verified: Buy now/View auction + a quiet "View details"
          text link. Unverified (one action): "View details" is the primary. */}
      {view !== undefined && view.mode !== 'unverified' ? (
        <div className="flex flex-col gap-2">
          <Link
            {...detailLink}
            data-testid="listing-card-primary"
            className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-700"
          >
            {listing.saleMechanic === 'auction' ? LISTING_CARD_VIEW_AUCTION : LISTING_CARD_BUY_NOW}
          </Link>
          <Link
            {...detailLink}
            className="text-center text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            {LISTING_CARD_VIEW_DETAILS}
          </Link>
        </div>
      ) : (
        <Link
          {...detailLink}
          className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-700"
        >
          {LISTING_CARD_VIEW_DETAILS}
        </Link>
      )}
    </Card>
  );
}
